import React, {useEffect, useRef} from "react";
import ReactDOM from "react-dom";
import "./Modal.scss";

// Shared across all modal instances so stacked modals close one per ESC
// and the body scroll lock survives until the last modal closes.
const modalStack = [];
let scrollLockCount = 0;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])';

export default function Modal({isOpen, onClose, isDark, ariaLabel, children}) {
  const contentRef = useRef(null);
  const instanceRef = useRef({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const instance = instanceRef.current;
    modalStack.push(instance);
    const previouslyFocused = document.activeElement;
    if (contentRef.current) {
      contentRef.current.focus();
    }

    const handleKeyDown = event => {
      if (modalStack[modalStack.length - 1] !== instance) {
        return; // only the top-most modal reacts
      }
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "Tab" && contentRef.current) {
        const focusable = contentRef.current.querySelectorAll(FOCUSABLE);
        if (focusable.length === 0) {
          event.preventDefault();
          contentRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === contentRef.current)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    // When focus moves into an embedded iframe (e.g. the resume PDF), the
    // window fires "blur" and document-level key handling stops working.
    // Pull focus back to the dialog so ESC keeps closing it.
    const handleWindowBlur = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (
          modalStack[modalStack.length - 1] === instance &&
          active &&
          active.tagName === "IFRAME" &&
          contentRef.current &&
          contentRef.current.contains(active)
        ) {
          contentRef.current.focus();
        }
      }, 0);
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("blur", handleWindowBlur);
    scrollLockCount += 1;
    if (scrollLockCount === 1) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("blur", handleWindowBlur);
      const index = modalStack.indexOf(instance);
      if (index !== -1) {
        modalStack.splice(index, 1);
      }
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        document.body.style.overflow = "";
      }
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className={isDark ? "modal-content modal-content-dark" : "modal-content"}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        ref={contentRef}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={isDark ? "modal-close modal-close-dark" : "modal-close"}
          onClick={onClose}
          aria-label="Close dialog"
        >
          ×
        </button>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}
